import React from 'react'
import { createStore, compose } from 'redux'
import { Provider } from 'react-redux'

import rootReducer from './reducers'
import middleware from 'middleware'
import {
  awaitNewTicket,
  fetchMemberCommsMessages,
  fetchUnresolvedMessages,
  INBOX_INDEXING_DELAY_MS,
} from 'store/MemberCommunication'

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
export const store = createStore(rootReducer, composeEnhancers(middleware))

/**
 * Query param member-app sets when Ash's "View your message" hand-off has to
 * cross the MFE boundary. See `applyPendingTicketWait`.
 */
export const PENDING_TICKET_PARAM = 'pendingTicket'

/**
 * Carry "a ticket was just filed" across a hard navigation into the SPA.
 *
 * Inside the SPA, `SidekickProvider` dispatches `awaitNewTicket()` itself and
 * the store survives the client-side navigate. From member-app it cannot:
 * `/inbox` isn't a member-app route, so that handler does
 * `window.location.assign` — the document is torn down and this store is built
 * fresh, with `awaitingNewTicket` back to `false`. `RequestList` then fetches
 * immediately on mount (its gate is `requests.length === 0 &&
 * !awaitingNewTicket`, which is exactly a new store), sees a list that predates
 * the ticket, and renders it as settled content.
 *
 * So member-app appends `?pendingTicket=1` and this consumes it. Dispatched
 * here, at module scope, rather than from a component: this runs when `store`
 * is first imported, which is before anything mounts, so it cannot race
 * `RequestList`'s mount-time fetch. An effect in `Inbox/Home` would be a
 * sibling of that fetch, not ahead of it.
 *
 * The param is stripped with `replaceState` so a refresh or a shared URL
 * doesn't put the inbox back into waiting for a ticket that arrived long ago. A
 * URL is used rather than `sessionStorage` because it dies with the navigation
 * it belongs to — a stale storage key would be a bug that outlives its cause.
 *
 * The flag and the fetch have to be set up together. `awaitingNewTicket` is
 * only ever cleared by a fetch landing, so setting it without scheduling one
 * holds the inbox on a skeleton for the rest of the session.
 */
export const applyPendingTicketWait = () => {
  const { search, pathname, hash } = window.location
  const params = new URLSearchParams(search)
  if (!params.get(PENDING_TICKET_PARAM)) return false

  store.dispatch(awaitNewTicket())
  setTimeout(() => {
    store.dispatch(fetchMemberCommsMessages())
    store.dispatch(fetchUnresolvedMessages())
  }, INBOX_INDEXING_DELAY_MS)

  params.delete(PENDING_TICKET_PARAM)
  const remaining = params.toString()
  window.history.replaceState(
    window.history.state,
    '',
    `${pathname}${remaining ? `?${remaining}` : ''}${hash}`,
  )
  return true
}

applyPendingTicketWait()

export default ({ children }) => <Provider store={store}>{children}</Provider>
